import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
	Box,
	Card,
	Chip,
	Container,
	IconButton,
	LinearProgress,
	Popover,
	Stack,
	SvgIcon,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import EllipsisVerticalIcon from '@heroicons/react/24/solid/EllipsisVerticalIcon';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import PencilIcon from '@heroicons/react/24/solid/PencilIcon';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import { useSelection } from '../../hooks/use-selection';
import { Scrollbar } from '../../components/layouts/scrollbar';
import { AlertDialogSlide } from '../../components/generic/generic.confirm';
import { GenericMenuItem } from '../../components/generic/generic.menu.item';
import { AddButtonComponent } from '../../components/button';
import { DATE_FORMAT } from '../../constants';
import { neutral } from '../../theme/colors';
import { palette } from '../../components/report/report-theme';
import { Customer, PaginatedCustomers, QueryCustomerListArgs } from '../../gql/graphql';
import { DETELE_CUSTOMER, CUSTOMER_LIST } from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';

export const PAGE_SIZES = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;
/** Longest text the API accepts in a filter. */
const FILTER_MAX_LENGTH = 100;

/**
 * The filters; a customer must match every one that is filled in. `from` and `to` bound the
 * Created date and are calendar days written YYYY-MM-DD, or '' for an open end.
 */
interface Filters {
	name: string;
	phone: string;
	email: string;
	from: string;
	to: string;
}
const FILTER_KEYS: (keyof Filters)[] = ['name', 'phone', 'email', 'from', 'to'];
const NO_FILTERS: Filters = { name: '', phone: '', email: '', from: '', to: '' };
const DAY = 'YYYY-MM-DD';

/** A YYYY-MM-DD day from the URL, or '' when it is missing or not a real date. */
const readDay = (value: string | null): string =>
	value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value, DAY).isValid() ? value : '';

interface RangePreset {
	key: string;
	label: string;
	/** null means no date limit. */
	range: () => [Dayjs, Dayjs] | null;
}

/** The quick ranges of the Reports filter (components/report/search.tsx), with "All time" first. */
const PRESETS: RangePreset[] = [
	{ key: 'all', label: 'All time', range: () => null },
	{ key: 'today', label: 'Today', range: () => [dayjs().startOf('day'), dayjs().endOf('day')] },
	{ key: 'yesterday', label: 'Yesterday', range: () => [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
	{ key: '7d', label: 'Last 7 days', range: () => [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
	{ key: '30d', label: 'Last 30 days', range: () => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
	{ key: 'mtd', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('day')] },
	{ key: 'lastMonth', label: 'Last month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
	{ key: '12m', label: 'Last 12 months', range: () => [dayjs().subtract(11, 'month').startOf('month'), dayjs().endOf('day')] },
];

/** The from/to days a preset stands for. */
const presetDays = (preset: RangePreset): Pick<Filters, 'from' | 'to'> => {
	const range = preset.range();
	return range ? { from: range[0].format(DAY), to: range[1].format(DAY) } : { from: '', to: '' };
};

interface View extends Filters {
	/** 1-based page number. */
	page: number;
	size: number;
}

const trimFilters = (filters: Filters): Filters => ({
	name: filters.name.trim(),
	phone: filters.phone.trim(),
	email: filters.email.trim(),
	from: filters.from,
	to: filters.to,
});

const sameFilters = (a: Filters, b: Filters) => FILTER_KEYS.every((key) => a[key] === b[key]);

/** Reads the filters, page and page size from the URL, falling back to defaults for anything missing or invalid. */
const readView = (params: URLSearchParams): View => {
	const page = parseInt(params.get('page') ?? '', 10);
	const size = parseInt(params.get('size') ?? '', 10);
	return {
		...trimFilters({
			name: params.get('name') ?? '',
			phone: params.get('phone') ?? '',
			email: params.get('email') ?? '',
			from: readDay(params.get('from')),
			to: readDay(params.get('to')),
		}),
		page: page > 0 ? page : 1,
		size: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
	};
};

/** Writes a view back as URL parameters, leaving out whatever is empty or at its default. */
const writeView = (view: View): URLSearchParams => {
	const params = new URLSearchParams();
	FILTER_KEYS.forEach((key) => {
		if (view[key]) params.set(key, view[key]);
	});
	if (view.page > 1) params.set('page', String(view.page));
	if (view.size !== DEFAULT_PAGE_SIZE) params.set('size', String(view.size));
	return params;
};

const formatCreated = (createdAt?: string | null) => (createdAt ? format(new Date(createdAt), DATE_FORMAT) : '');

const COLUMN_COUNT = 6;
/** Every filter field takes an equal share of the row, so the five fit on one line on a desktop. */
const FIELD_SX = { flex: '1 1 0', minWidth: 150 };

function CustomerList(props: any) {
	const navigate = useNavigate();
	const { shopId, locationId } = useShopDetails();

	// The filters, page and size live in the URL, so returning from editing a customer or
	// reloading shows the same view. Replace rather than push, so Back leaves the page
	// instead of stepping through every keystroke.
	const [searchParams, setSearchParams] = useSearchParams();
	const view = readView(searchParams);
	const { page, size } = view;
	const applied: Filters = { name: view.name, phone: view.phone, email: view.email, from: view.from, to: view.to };
	const hasFilters = FILTER_KEYS.some((key) => applied[key]);
	const updateView = useCallback(
		(change: Partial<View>) => {
			setSearchParams((current) => writeView({ ...readView(current), ...change }), { replace: true });
		},
		[setSearchParams]
	);

	// The boxes hold what is typed. Nothing is sent while typing: the filters are applied, and the
	// server asked, only when Apply is pressed (or Enter in a box), as on the Reports page.
	const [typed, setTyped] = useState<Filters>(applied);

	const { data, previousData, loading, error, refetch } = useQuery<
		{ customerList: PaginatedCustomers },
		QueryCustomerListArgs
	>(CUSTOMER_LIST, {
		variables: {
			input: {
				shopId,
				locationId,
				name: applied.name || undefined,
				phone: applied.phone || undefined,
				email: applied.email || undefined,
				// Whole calendar days in the browser's time zone, as the Created column shows them.
				createdFrom: applied.from ? dayjs(applied.from, DAY).startOf('day').toISOString() : undefined,
				createdTo: applied.to ? dayjs(applied.to, DAY).endOf('day').toISOString() : undefined,
				page,
				limit: size,
			},
		},
		skip: !shopId || !locationId,
		// The app's default ignores errors, which would show a refused request as an empty list.
		errorPolicy: 'all',
	});
	/** Applies what is typed and asks the server for the first page that matches. */
	const handleSearch = useCallback(() => {
		const next = trimFilters(typed);
		if (next.from && next.to && next.from > next.to) {
			// Same check and wording as the Reports filter.
			toast.error('From date cannot be greater than To date');
			return;
		}
		if (sameFilters(next, applied) && page === 1) {
			// Same filters, same page: the URL would not change, so ask the server again directly.
			refetch();
			return;
		}
		updateView({ ...next, page: 1 });
		// eslint-disable-next-line
	}, [typed, page, updateView, refetch, view.name, view.phone, view.email, view.from, view.to]);

	/** A quick range is a complete intent: set its days and search at once, as on Reports. */
	const handlePreset = (preset: RangePreset) => {
		const next = { ...trimFilters(typed), ...presetDays(preset) };
		setTyped(next);
		updateView({ ...next, page: 1 });
	};
	// The chip that matches the days in the pickers, if any.
	const activePreset = PRESETS.find((preset) => {
		const days = presetDays(preset);
		return days.from === typed.from && days.to === typed.to;
	})?.key;

	const dayPicker = (key: 'from' | 'to', label: string) => (
		<DatePicker
			label={label}
			value={typed[key] ? dayjs(typed[key], DAY) : null}
			onChange={(value: Dayjs | null) =>
				setTyped((current) => ({ ...current, [key]: value && value.isValid() ? value.format(DAY) : '' }))
			}
			slotProps={{ textField: { size: 'small', sx: FIELD_SX, inputProps: { 'aria-label': label } } }}
		/>
	);

	// Text typed but not yet searched for: the list below still shows the previous search.
	const unapplied = !sameFilters(trimFilters(typed), applied);

	const handleClear = useCallback(() => {
		setTyped(NO_FILTERS);
		updateView({ ...NO_FILTERS, page: 1 });
	}, [updateView]);

	// Keep the last page on screen while the next one loads, so the rows do not flash empty.
	const result = (data ?? previousData)?.customerList;
	// A failed request shows its error instead of the rows of the last request that worked.
	const customers = error ? [] : result?.customers ?? [];
	const total = error ? 0 : result?.total ?? 0;

	// A page past the end (the last row of the last page was deleted, or an old link) moves to the last page.
	useEffect(() => {
		const current = data?.customerList;
		if (current && current.customers.length === 0 && current.total > 0 && page > 1) {
			updateView({ page: current.totalPages });
		}
	}, [data, page, updateView]);

	const customersSelection = useSelection(customers as any);

	const [deleteCustomer] = useMutation(DETELE_CUSTOMER);
	const [menuAnchor, setMenuAnchor] = useState<(HTMLElement & { data?: Customer }) | null>(null);
	const [pendingDelete, setPendingDelete] = useState<Customer | null>(null);

	const handleDeleteConfirmed = useCallback(async () => {
		const customer = pendingDelete;
		setPendingDelete(null);
		if (!customer) {
			return;
		}
		await deleteCustomer({ variables: { customerId: customer._id, shopId } });
		toast.success(`Deleted ${customer.firstName} ${customer.lastName} successfully.`);
		await refetch();
	}, [pendingDelete, deleteCustomer, shopId, refetch]);

	const handleAddEdit = useCallback(
		(customer?: Customer) => {
			if (customer?._id) {
				navigate(`/dashboard/customers/${customer._id}/edit`);
			} else {
				navigate('/dashboard/customers/new');
			}
		},
		[navigate]
	);

	const emptyMessage = error
		? `Could not load customers: ${error.message}`
		: hasFilters
			? 'No customer matches these filters.'
			: 'No customers yet.';

	const filterBox = (key: keyof Filters, label: string, extra: { inputMode?: 'tel' | 'email' } = {}) => (
		<TextField
			label={label}
			value={typed[key]}
			onChange={(event) => setTyped((current) => ({ ...current, [key]: event.target.value }))}
			size="small"
			sx={FIELD_SX}
			inputProps={{ maxLength: FILTER_MAX_LENGTH, ...extra }}
		/>
	);

	return (
		<>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 8
				}}
			>
				<Container maxWidth="xl">
					<Stack spacing={3}>
						{pendingDelete && (
							<AlertDialogSlide
								open
								onAgree={handleDeleteConfirmed}
								onDisagree={() => setPendingDelete(null)}
								description='Are you sure want to delete ?'
							/>
						)}
						<Stack direction="row" justifyContent="space-between" spacing={4}>
							<Typography variant="h4">Customers</Typography>
							{!props.isModal && <AddButtonComponent onClick={() => handleAddEdit()} />}
						</Stack>
						{/* Same card as the Reports, Orders and Commission filters (components/report/search.tsx). */}
						<Card elevation={0} sx={{ border: `1px solid ${neutral[200]}`, borderRadius: 2 }}>
							<form
								role="search"
								aria-label="Customer filters"
								noValidate
								onSubmit={(event) => {
									event.preventDefault();
									handleSearch();
								}}
							>
								<Stack spacing={2.5} sx={{ p: 2.5 }}>
									<Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
										<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
											QUICK RANGE
										</Typography>
										{PRESETS.map((preset) => (
											<Chip
												key={preset.key}
												label={preset.label}
												size="small"
												onClick={() => handlePreset(preset)}
												aria-pressed={activePreset === preset.key}
												sx={{
													borderRadius: 1.5,
													fontWeight: 500,
													backgroundColor: activePreset === preset.key ? palette.single : neutral[100],
													color: activePreset === preset.key ? '#fff' : 'text.primary',
													'&:hover': {
														backgroundColor: activePreset === preset.key ? palette.single : neutral[200],
													},
												}}
											/>
										))}
									</Stack>
									<Stack
										direction={{ xs: 'column', md: 'row' }}
										alignItems={{ xs: 'stretch', md: 'center' }}
										spacing={2}
										useFlexGap
										flexWrap="wrap"
									>
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											{dayPicker('from', 'Created from')}
											{dayPicker('to', 'Created to')}
										</LocalizationProvider>
										{filterBox('name', 'Name')}
										{filterBox('phone', 'Phone', { inputMode: 'tel' })}
										{filterBox('email', 'Email', { inputMode: 'email' })}
										<Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
											<LoadingButton
												type="submit"
												loading={loading}
												startIcon={<SvgIcon><SearchIcon /></SvgIcon>}
												variant="contained"
											>
												Apply
											</LoadingButton>
											<Button
												variant="outlined"
												onClick={handleClear}
												disabled={!hasFilters && FILTER_KEYS.every((key) => !typed[key])}
											>
												Clear
											</Button>
										</Stack>
									</Stack>
									{unapplied && (
										<Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
											Press Apply or Enter to search.
										</Typography>
									)}
								</Stack>
							</form>
						</Card>
						<Card>
							{loading && <LinearProgress aria-label="Loading customers" />}
							<Scrollbar>
								<Box sx={{ minWidth: 800 }}>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>First Name</TableCell>
												<TableCell>Last Name</TableCell>
												<TableCell>Phone</TableCell>
												<TableCell>Email</TableCell>
												<TableCell>Created</TableCell>
												<TableCell>Action</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{!result && !error && loading && (
												<TableRow>
													<TableCell colSpan={COLUMN_COUNT} align="center">Loading customers…</TableCell>
												</TableRow>
											)}
											{customers.length === 0 && (error || (result && !loading)) && (
												<TableRow>
													<TableCell colSpan={COLUMN_COUNT} align="center">{emptyMessage}</TableCell>
												</TableRow>
											)}
											{customers.map((customer) => (
												<TableRow
													hover
													key={customer._id}
													selected={(customersSelection.selected as Customer | null)?._id === customer._id}
												>
													<TableCell>{customer.firstName}</TableCell>
													<TableCell>{customer.lastName}</TableCell>
													<TableCell>{customer.phone}</TableCell>
													<TableCell>{customer.email}</TableCell>
													<TableCell>{formatCreated(customer.createdAt)}</TableCell>
													<TableCell>
														<IconButton
															size="large"
															color="inherit"
															aria-label={`Actions for ${customer.firstName} ${customer.lastName ?? ''}`.trim()}
															onClick={(event: SyntheticEvent) => {
																const anchor = event.currentTarget as HTMLElement & { data?: Customer };
																anchor.data = customer;
																setMenuAnchor(anchor);
															}}
														>
															<SvgIcon color="action" fontSize="small">
																<EllipsisVerticalIcon />
															</SvgIcon>
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</Box>
							</Scrollbar>
							<Popover
								open={Boolean(menuAnchor)}
								anchorEl={menuAnchor}
								onClose={() => setMenuAnchor(null)}
								anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
								PaperProps={{
									sx: {
										p: 1,
										width: 140,
										'& .MuiMenuItem-root': {
											px: 1,
											typography: 'body2',
											borderRadius: 0.75,
										},
									},
								}}
							>
								<GenericMenuItem
									onClick={() => { const customer = menuAnchor?.data; setMenuAnchor(null); if (!props.isModal) handleAddEdit(customer); }}
									icon={PencilIcon}
									label='Edit'
								/>
								<GenericMenuItem
									onClick={() => { const customer = menuAnchor?.data; setMenuAnchor(null); if (customer) setPendingDelete(customer); }}
									icon={TrashIcon}
									label='Delete'
								/>
							</Popover>
							{result && (
								<TablePagination
									component="div"
									count={total}
									page={result && !error ? Math.min(page - 1, Math.max(result.totalPages - 1, 0)) : 0}
									rowsPerPage={size}
									rowsPerPageOptions={PAGE_SIZES}
									onPageChange={(_event, newPage) => updateView({ page: newPage + 1 })}
									onRowsPerPageChange={(event) => updateView({ size: parseInt(event.target.value, 10), page: 1 })}
								/>
							)}
						</Card>
						{props.isModal && <CardActions sx={{ justifyContent: 'flex-end' }} >
							<Button variant="outlined" onClick={() => { props.onClose() }} >
								Cancel
							</Button>
							<LoadingButton disabled={!customersSelection.selected} variant="contained" onClick={() => { props.onClose(customersSelection.selected) }}>
								Attach Selected
							</LoadingButton>
						</CardActions>}
					</Stack>
				</Container>
			</Box>
		</>
	);
}

export default CustomerList;
